import { useState } from "react";
import { useFetch } from "../../../common/hooks/useFetch";
import { saveFile } from "../../../common/utils/saveFile.util.js";
import Swal from "sweetalert2";
import { createMaintenanceService } from "../services/createMaintenance.service.js";
import { updateMaintenanceService } from "../services/updateMaintenance.service.js";
import { exportMaintenanceToExcelService } from "../services/exportMaintenanceToExcel.service.js";
import { updateDoneStatusService } from "../services/updateDoneStatus.service.js";
import { deleteMaintenanceService } from "../services/deleteMaintenance.service.js";

const initialState = {
  plate: "",
  type: "",
  date: "",
  mileage: "",
  observations: null,
  nextPreventiveMaintenance: null,
  numberInvoice: "",
  provider: "",
  valueInvoice: "",
  nameManager: "",
  identificationManager: "",
  roleManager: "",
  nitCompany: "",
  addressCompany: "",
};

export const useMaintenanceCommand = (refresh) => {
  const fetchData = useFetch();
  const [maintenance, setMaintenance] = useState(initialState);
  const [errors, setErrors] = useState({ hasError: false, errors: {} });
  const [loading, setLoading] = useState(false);

  const handleChange = ({ name, value }) => {
    setMaintenance((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const setDefaultValues = (data) => {
    setMaintenance(data);
  };

  const resetForm = () => {
    setMaintenance(initialState);
    setErrors({ hasError: false, errors: {} });
  };

  const createMaintenance = async () => {
    try {
      const isValid = validations(maintenance);
      if (!isValid) return;
      const response = await createMaintenanceService(fetchData)(maintenance);
      setMaintenance(initialState);
      setErrors({ hasError: false, errors: {} });
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Exito",
        text: response.message,
      });
      return true;
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateMaintenance = async () => {
    try {
      const isValid = validations(maintenance);
      if (!isValid) return;
      setLoading(true);
      const response = await updateMaintenanceService(fetchData)(maintenance);
      setMaintenance(initialState);
      setErrors({ hasError: false, errors: {} });
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Exito",
        text: response.message,
      });

      return true;
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async (filters) => {
    try {
      const response = await exportMaintenanceToExcelService(fetchData)(
        filters
      );
      saveFile(response, "mantenimiento.xlsx");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo exportar a Excel",
      });
    }
  };

  const updateDoneStatus = async (maintenance) => {
    try {
      if (!maintenance.id) {
        throw new Error("El mantenimiento no tiene id");
      }

      if (typeof maintenance.done !== "boolean") {
        throw new Error("El estado 'realizado' debe ser booleano");
      }

      const isSure = await Swal.fire({
        title: "¿Estás seguro?",
        text: `¿Deseas marcar el mantenimiento de la placa ${maintenance.plate} como realizado?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, marcar como realizado",
        cancelButtonText: "Cancelar",
      });

      if (!isSure.isConfirmed) {
        return;
      }

      setLoading(true);
      const response = await updateDoneStatusService(fetchData)(maintenance);
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Exito",
        text: response.message,
      });
      return true;
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteMaintenance = async (maintenance) => {
    try {
      if (!maintenance.id) {
        throw new Error("El mantenimiento no tiene id");
      }

      const isSure = await Swal.fire({
        title: "¿Estás seguro?",
        text: `¿Deseas eliminar permanentemente el mantenimiento de la placa ${maintenance.plate}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (!isSure.isConfirmed) {
        return;
      }

      setLoading(true);
      await deleteMaintenanceService(fetchData)(maintenance.id);
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        text: "El mantenimiento ha sido eliminado exitosamente",
      });
      return true;
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const validations = (maintenance) => {
    const newErrors = {};

    if (!maintenance.plate) {
      newErrors.plate = "La placa es requerida";
    }

    if (!maintenance.type) {
      newErrors.type = "El tipo es requerido";
    }

    if (!maintenance.date) {
      newErrors.date = "La fecha es requerida";
    }

    if (
      maintenance.mileage === "" ||
      maintenance.mileage === null ||
      maintenance.mileage === undefined
    ) {
      newErrors.mileage = "El kilometraje es requerido";
    }

    if (
      maintenance.type === "preventivo" &&
      !maintenance.nextPreventiveMaintenance
    ) {
      newErrors.nextPreventiveMaintenance =
        "El siguiente mantenimiento preventivo es requerido";
    }

    if (!maintenance.numberInvoice) {
      newErrors.numberInvoice = "El número de factura es requerido";
    }

    if (!maintenance.provider) {
      newErrors.provider = "El proveedor es requerido";
    }

    if (
      maintenance.valueInvoice === "" ||
      maintenance.valueInvoice === null ||
      maintenance.valueInvoice === undefined
    ) {
      newErrors.valueInvoice = "El valor de la factura es requerido";
    }

    if (!maintenance.nameManager) {
      newErrors.nameManager = "El nombre del responsable es requerido";
    }

    if (!maintenance.identificationManager) {
      newErrors.identificationManager =
        "La identificación del responsable es requerida";
    }

    if (!maintenance.roleManager) {
      newErrors.roleManager = "El rol del responsable es requerido";
    }

    if (!maintenance.nitCompany) {
      newErrors.nitCompany = "El NIT de la empresa es requerido";
    }

    if (!maintenance.addressCompany) {
      newErrors.addressCompany = "La dirección de la empresa es requerida";
    }

    const hasErrors = Object.keys(newErrors).length > 0;
    setErrors({ hasError: hasErrors, errors: newErrors });
    return !hasErrors;
  };

  return {
    maintenance,
    handleChange,
    setDefaultValues,
    resetForm,
    createMaintenance,
    updateMaintenance,
    exportToExcel,
    updateDoneStatus,
    deleteMaintenance,
    errors,
    loading,
    validations,
  };
};
